import 'react-international-phone/style.css';
import Box from '@mui/material/Box';
import { Button, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { PsiService } from '@inversor-hub/types';
import OpportunityPreview from '@/features/opportunities/OpportunityPreview.tsx';
import { toOpportunityFormData } from '@/features/opportunities/form/utils';
import { useGetOpportunity } from '@/lib/api/opportunities';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider';
import {
  useCreateOpportunityInterest,
  useDeleteOpportunityInterest,
  useListInvestorOpportunityInterests,
} from '@/lib/api/opportunity-interests';
import { useCreateOpportunityAnalysis } from '@/lib/api/opportunities';
import { useAuth } from '@/hooks/useAuth';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import AIAnalysisCard from '@/components/ai/AIAnalysisCard';
import { SimpleInterestModal } from '@/features/opportunities/SimpleInterestModal';
import { useEffect, useState } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { QUICK_GUIDE_STATUS_KEY } from '@/lib/api/me';
import {
  ANALYTICS_PLATFORM,
  trackProductEvent,
  trackProductEventOncePerSession,
} from '@/lib/analytics/product-events';

const OpportunityDetailPage = () => {
  const { id } = useParams();
  const opportunityId = Array.isArray(id) ? undefined : id;
  const { data: opportunity } = useGetOpportunity(opportunityId);
  const { data: opportunityInterests } = useListInvestorOpportunityInterests();
  const { mutateAsync: createOpportunityInterest, isPending: isCreatingInterest } =
    useCreateOpportunityInterest();
  const { mutateAsync: deleteOpportunityInterest } = useDeleteOpportunityInterest();
  const {
    mutateAsync: analyseOpportunity,
    data: fitAnalysis,
    isPending: isCreatingAnalysis,
  } = useCreateOpportunityAnalysis();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [showInterestModal, setShowInterestModal] = useState(false);
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!opportunityId || !opportunity || user?.role !== 'investor') {
      return;
    }

    queryClient.invalidateQueries({ queryKey: [QUICK_GUIDE_STATUS_KEY] });

    trackProductEventOncePerSession(
      trackEvent,
      `opportunity_viewed:${opportunityId}`,
      'opportunity_viewed',
      {
        opportunity_id: opportunityId,
        role: 'investor',
        platform: ANALYTICS_PLATFORM,
      }
    );
  }, [opportunity, opportunityId, queryClient, trackEvent, user?.role]);

  const interest = opportunityInterests?.find(
    (opportunityInterest) =>
      opportunityInterest.opportunityId === id && opportunityInterest.status !== 'REMOVED'
  );

  const handleToggleInterest = async () => {
    if (interest) {
      await deleteOpportunityInterest(opportunityId!);
      return;
    }
    setShowInterestModal(true);
  };

  const handleConfirmInterest = async (selectedServices: PsiService[]) => {
    await createOpportunityInterest({ opportunityId: opportunityId!, selectedServices });
    trackProductEvent(trackEvent, 'interest_sent', {
      opportunity_id: opportunityId!,
      entry_point: 'opportunity_detail',
      selected_services_count: selectedServices.length,
      role: 'investor',
      platform: ANALYTICS_PLATFORM,
    });
    setShowInterestModal(false);
  };

  const handleAnalyzeWithAI = async () => {
    await analyseOpportunity(opportunityId!);
  };

  return (
    <Box sx={{ width: '100%', height: 'auto', display: 'flex', flexDirection: 'column', pb: 12 }}>
      <GoogleMapsProvider>
        {opportunity && (
          <>
            {user?.role === 'investor' && (
              <Box sx={{ mb: 4, maxWidth: 600, alignSelf: 'center', width: '100%' }}>
                <AIAnalysisCard
                  onAnalyze={handleAnalyzeWithAI}
                  analysis={fitAnalysis}
                  isAnalyzing={isCreatingAnalysis}
                />
              </Box>
            )}
            <OpportunityPreview
              data={toOpportunityFormData(opportunity)}
              opportunityId={opportunityId!}
              mainImageId={opportunity.mainImageId}
            />

            {/* Sticky CTA Bar */}
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: isMobile ? 0 : '270px',
                right: 0,
                zIndex: 1200,
                background: interest
                  ? `linear-gradient(135deg, ${theme.palette.tokens.secondary[500]} 0%, ${theme.palette.tokens.primary[800]} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.tokens.primary[500]} 0%, ${theme.palette.tokens.primary[700]} 100%)`,
                backdropFilter: 'blur(10px)',
                boxShadow: theme.shadows[8],
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <Container
                maxWidth="md"
                sx={{
                  py: 2.5,
                  px: 3,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      mb: 0.5,
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    {interest ? '¡Ya mostraste interés!' : '¿Te interesa esta oportunidad?'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.95)',
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    }}
                  >
                    {interest
                      ? 'El PSI puede contactarte pronto'
                      : 'Hazle saber al PSI que estás interesado'}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  onClick={handleToggleInterest}
                  size="large"
                  disabled={interest?.status === 'REJECTED'}
                  sx={{
                    minWidth: { xs: '100%', sm: 280 },
                    py: 1.75,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    bgcolor: 'white',
                    color: 'primary.main',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                    textTransform: 'none',
                    borderRadius: 3,
                    border: `2px solid ${theme.palette.tokens.primary[400]}`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'white',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                      transform: 'translateY(-2px)',
                      borderColor: theme.palette.tokens.primary[500],
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(255, 255, 255, 0.5)',
                      color: 'rgba(0, 0, 0, 0.4)',
                      border: '2px solid rgba(0, 0, 0, 0.15)',
                      boxShadow: 'none',
                      cursor: 'not-allowed',
                      opacity: 0.7,
                    },
                  }}
                >
                  {interest ? 'Ya no me interesa' : 'Me interesa'}
                </Button>
              </Container>
            </Box>

            <SimpleInterestModal
              open={showInterestModal}
              onClose={() => setShowInterestModal(false)}
              onConfirm={handleConfirmInterest}
              psiId={opportunity.psiId}
              loading={isCreatingInterest}
            />
          </>
        )}
      </GoogleMapsProvider>
    </Box>
  );
};

export default OpportunityDetailPage;
